/* ServicesAdoptionWidget — admin-overview pulse for the 4 Invisique services.
 *
 * iter100.239 — 4-tile row surfacing 30-day adoption of the four services
 * tracked on the Product Analytics page:
 *   • IO Scans by Invisique             (rose)
 *   • Specialist Counsellor Callback    (amber)
 *   • Counsellor Team performance       (emerald · reach rate)
 *   • Invisique Compliance Monitoring   (teal · opt-in rate)
 *
 * Each tile shows a headline metric, a period-over-period delta pill,
 * and deep-links to the matching tab on /admin/analytics for drill-in.
 *
 * Data source: same 4 admin analytics endpoints powering Analytics.jsx.
 * All fetched in parallel via Promise.allSettled so one failing endpoint
 * doesn't blank the whole widget.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
    Camera, Phone, Headset, Pulse, ArrowUpRight,
} from "@phosphor-icons/react";
import AdminSectionShell from "@/components/admin/AdminSectionShell";

const SERVICES = [
    {
        endpoint: "scan-visit-funnel",
        label: "IO Scans by Invisique",
        primaryKey: "confirmations",
        valueSuffix: "",
        Icon: Camera,
        tone: {
            accent: "text-rose-700",
            bg: "bg-rose-50/60",
            hoverBg: "hover:bg-rose-50",
            border: "border-rose-200/70",
            hoverBorder: "hover:border-rose-300",
        },
        subLabel: "Technician-visit bookings",
        tab: "acquisition",
    },
    {
        endpoint: "callback-enrichment-funnel",
        label: "Specialist Counsellor Callback",
        primaryKey: "total_callbacks",
        valueSuffix: "",
        Icon: Phone,
        tone: {
            accent: "text-amber-700",
            bg: "bg-amber-50/60",
            hoverBg: "hover:bg-amber-50",
            border: "border-amber-200/70",
            hoverBorder: "hover:border-amber-300",
        },
        subLabel: "Callback requests",
        tab: "acquisition",
    },
    {
        endpoint: "contact-outcome-mix",
        label: "Counsellor Team",
        primaryKey: "reach_rate_pct",
        valueSuffix: "%",
        Icon: Pulse,
        tone: {
            accent: "text-emerald-700",
            bg: "bg-emerald-50/60",
            hoverBg: "hover:bg-emerald-50",
            border: "border-emerald-200/70",
            hoverBorder: "hover:border-emerald-300",
        },
        subLabel: "Reach rate",
        tab: "ops",
    },
    {
        endpoint: "coe-support-optin",
        label: "Invisique Compliance Monitoring",
        primaryKey: "opt_in_pct",
        valueSuffix: "%",
        Icon: Headset,
        tone: {
            accent: "text-teal-700",
            bg: "bg-teal-50/60",
            hoverBg: "hover:bg-teal-50",
            border: "border-teal-200/70",
            hoverBorder: "hover:border-teal-300",
        },
        subLabel: "Doctor opt-in rate",
        tab: "product",
    },
];


/**
 * Same delta grammar used inside /admin/analytics. Copied instead of imported
 * so this widget stays independent — Analytics.jsx doesn't export any of its
 * primitives.
 */
function DeltaPill({ current, previous }) {
    if (current == null || previous == null) return null;
    if (previous === 0) {
        if (!current) return null;
        return (
            <span className="inline-flex items-center text-[9.5px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200/70">
                NEW
            </span>
        );
    }
    const delta = Math.round(((current - previous) / previous) * 1000) / 10;
    if (Math.abs(delta) < 0.5) {
        return (
            <span className="inline-flex items-center text-[9.5px] font-semibold px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border">
                ─ flat
            </span>
        );
    }
    const up = delta > 0;
    const cls = up
        ? "bg-emerald-100 text-emerald-800 border-emerald-200/70"
        : "bg-rose-100 text-rose-700 border-rose-200/70";
    return (
        <span
            className={`inline-flex items-center gap-0.5 text-[9.5px] font-bold tabular-nums px-1.5 py-0.5 rounded-full border ${cls}`}
        >
            {up ? "▲" : "▼"} {Math.abs(delta)}%
        </span>
    );
}


export default function ServicesAdoptionWidget() {
    const [byEndpoint, setByEndpoint] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            const results = await Promise.allSettled(
                SERVICES.map((svc) =>
                    api.get(`/admin/analytics/${svc.endpoint}?days=30`).then((r) => [svc.endpoint, r.data]),
                ),
            );
            const map = {};
            for (const r of results) {
                if (r.status === "fulfilled") {
                    const [key, data] = r.value;
                    map[key] = data;
                }
            }
            setByEndpoint(map);
            setLoading(false);
        })();
    }, []);

    return (
        <AdminSectionShell
            eyebrow="Services adoption"
            tone="teal"
            description="30-day adoption pulse across every Invisique service."
            right={
                /* iter100.245 — Drop the "Product analytics" nav link.
                   Each tile already drills into the corresponding
                   /admin/analytics?tab=... panel; the header should
                   show context, not repeat navigation. */
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground tabular-nums" data-testid="services-adoption-count">
                    {SERVICES.length} services tracked
                </span>
            }
            bodyPad
            testid="admin-services-adoption"
        >
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {SERVICES.map((svc) => {
                    const d = byEndpoint[svc.endpoint];
                    const current = d?.[svc.primaryKey];
                    const previous = d?.previous_period?.[svc.primaryKey];
                    const { Icon } = svc;
                    return (
                        <Link
                            key={svc.endpoint}
                            to={`/admin/analytics?tab=${svc.tab}`}
                            data-testid={`services-adoption-tile-${svc.endpoint}`}
                            /* iter100.252 — Neutralised base state so all
                               Overview sections share the Platform Pulse
                               grammar. Tone reveals only on hover. Icon
                               keeps its colour so admins can still tell
                               services apart at a glance. */
                            className={`group p-4 rounded-xl border border-border bg-card ${svc.tone.hoverBg} hover:border-primary/30 transition-colors overflow-hidden`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className={`text-[10.5px] font-bold uppercase tracking-[0.14em] leading-tight ${svc.tone.accent}`}>
                                    {svc.label}
                                </div>
                                <Icon size={16} weight="duotone" className={`${svc.tone.accent} flex-shrink-0`} />
                            </div>
                            <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                                <div className="font-display text-[24px] font-bold tabular-nums leading-none text-foreground">
                                    {loading ? "—" : (current ?? 0)}{svc.valueSuffix}
                                </div>
                                {!loading && <DeltaPill current={current} previous={previous} />}
                            </div>
                            <div className="mt-1.5 text-[10.5px] text-muted-foreground/80 inline-flex items-center gap-1 group-hover:text-muted-foreground transition-colors">
                                {svc.subLabel}
                                <ArrowUpRight size={10} weight="bold" className="opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                        </Link>
                    );
                })}
            </div>
        </AdminSectionShell>
    );
}
